import React, { useEffect, useState, useRef } from "react"
import { useNavigate, NavLink } from "react-router-dom"
import {
	Box,
	Center,
	Image,
	Heading,
	HStack,
	IconButton,
	Menu,
	MenuList,
	MenuItem,
	MenuButton,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Stack,
	Button,
	Text,
	Table,
	Tag,
	TagLabel,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	useDisclosure,
} from "@chakra-ui/react"
import { EditIcon, UpDownIcon } from "@chakra-ui/icons"
import { Select } from "chakra-react-select"
import {
	collection,
	endBefore,
	getDocs,
	query,
	where,
	startAfter,
	limit,
	limitToLast,
	orderBy,
} from "firebase/firestore"
import { db } from "../../firebase"

import { dateToFormat } from "../../Utilities"
import { EmptyState } from "../../Components/EmptyState"
import { VendorJobRequestListMobileCard } from "../../Components/VendorJobRequestListMobileCard"
import { PaywallBanner } from "../../Components/PaywallBanner"
import { SkeletonTable } from "../../Components/SkeletonTable"
import { BlackDomeStatuses } from "../../Options/BlackDomeStatuses"
import { BlackDomeStatusModal } from "../../Components/BlackDomeStatusModal"
import { Partners } from "../../partnershipData"

export const VendorJobRequestListView = ({ userPlus }) => {
	const { isOpen, onOpen, onClose } = useDisclosure()
	const navigate = useNavigate()
	const partner = Partners.find((p) => p.id === userPlus?.partner)

	// Keep state only for values that must trigger UI updates
	const [pageLoading, setPageLoading] = useState(true)
	const [jobRequests, setJobRequests] = useState([])
	const [jobResponsesRequestIds, setJobResponsesRequestIds] = useState([])
	const [statusModal, setStatusModal] = useState(false)
	const [activeJobRequest, setActiveJobRequest] = useState(null)

	// UI state for filters that need to trigger re-renders
	const [filtered, setFiltered] = useState(false)
	const [serviceTypeFilter, setServiceTypeFilter] = useState([])
	const [statusFilter, setStatusFilter] = useState("")
	const [blackdomeStatusFilter, setBlackdomeStatusFilter] = useState("")

	// Refs for data that should not trigger rerenders
	const pageRef = useRef(1)
	const pageSize = 10 // Constant, no need for state or ref
	const directionRef = useRef("next")
	const lastVisibleRef = useRef(null)
	const firstVisibleRef = useRef(null)
	const hasMoreRef = useRef(true)
	const clearFiltersRef = useRef(true)
	const vendorServicesRef = useRef([])
	const serviceTypesOptionsRef = useRef([])
	const jobRequestIdsRef = useRef([])

	// Refs for managing async operations
	const isFetchingRef = useRef(false)
	const timeoutRef = useRef(null)

	// Force update function for when we need to refresh UI after ref changes
	const [, forceUpdate] = useState({})
	const triggerRender = () => forceUpdate({})

	// Function to extract refactored fetch logic to avoid duplication
	const fetchJobRequestsData = async () => {
		isFetchingRef.current = true
		setPageLoading(true)

		try {
			// Fetch job requests data
			let jobRequestsQuery
			if (userPlus.partner && userPlus.isAdmin) {
				jobRequestsQuery = query(
					collection(db, "jobRequests"),
					where("partner", "==", userPlus.partner),
					orderBy("_createdAt"),
					...(directionRef.current === "next" && lastVisibleRef.current
						? [startAfter(lastVisibleRef.current)]
						: []),
					...(directionRef.current === "previous" && firstVisibleRef.current
						? [endBefore(firstVisibleRef.current)]
						: []),
					directionRef.current === "next"
						? limit(pageSize + 1)
						: limitToLast(pageSize + 1)
				)
			} else {
				jobRequestsQuery = query(
					collection(db, "jobRequests"),
					where("vendorIds", "array-contains", userPlus.id),
					orderBy("_createdAt"),
					...(directionRef.current === "next" && lastVisibleRef.current
						? [startAfter(lastVisibleRef.current)]
						: []),
					...(directionRef.current === "previous" && firstVisibleRef.current
						? [endBefore(firstVisibleRef.current)]
						: []),
					directionRef.current === "next"
						? limit(pageSize + 1)
						: limitToLast(pageSize + 1)
				)
			}

			const docs = await getDocs(jobRequestsQuery)

			// Update pagination state and job requests
			if (!docs.empty) {
				const jobRequestsData = docs.docs.slice(0, pageSize).map((doc) => ({
					id: doc.id,
					...doc.data(),
				}))

				// Store the pagination info in refs
				lastVisibleRef.current = docs.docs[docs.docs.length - 1]
				firstVisibleRef.current = docs.docs[0]
				hasMoreRef.current = docs.docs.length > pageSize

				// This needs to update the UI, so we use state
				setJobRequests(jobRequestsData)

				const jobRequestIds = docs.docs.map((doc) => doc.data().id)
				jobRequestIdsRef.current = jobRequestIds
			}
		} catch (error) {
			console.error("Error fetching data:", error)
		} finally {
			setPageLoading(false)
			isFetchingRef.current = false
		}
	}

	useEffect(() => {
		// Skip if userPlus is still pending
		if (userPlus === "pending") {
			return
		}

		const fetchData = async () => {
			// Prevent concurrent fetches
			if (isFetchingRef.current) return
			isFetchingRef.current = true

			// Only set loading state once
			setPageLoading(true)

			try {
				// Fetch services data
				let servicesQuery
				if (userPlus.partner && userPlus.isAdmin) {
					servicesQuery = query(
						collection(db, "services"),
						where("partner", "==", userPlus.partner)
					)
				} else {
					servicesQuery = query(
						collection(db, "services"),
						where("userId", "==", userPlus.id)
					)
				}
				const servicesDocs = await getDocs(servicesQuery)
				const services = servicesDocs.docs.map((doc) => doc.data().serviceType)
				vendorServicesRef.current = services

				const serviceTypesOptions = servicesDocs.docs.map((doc) => ({
					value: doc.data().serviceType,
					label: doc.data().serviceType,
				}))
				serviceTypesOptionsRef.current = serviceTypesOptions

				// Use the shared function to fetch job requests
				await fetchJobRequestsData()
			} catch (error) {
				console.error("Error fetching data:", error)
			} finally {
				// Reset flags and handle any cleanup
				setPageLoading(false)
				isFetchingRef.current = false

				// Handle clearFilters without triggering additional re-renders
				if (clearFiltersRef.current) {
					setTimeout(() => {
						clearFiltersRef.current = false
						pageRef.current = 1
						directionRef.current = "next"
						lastVisibleRef.current = null
						resetFilters()
					}, 0)
				}
			}
		}

		// Throttle fetchData to prevent multiple rapid executions
		clearTimeout(timeoutRef.current)
		timeoutRef.current = setTimeout(() => {
			fetchData()
		}, 200)

		return () => {
			clearTimeout(timeoutRef.current)
		}
	}, [userPlus]) // Remove pageRef.current and clearFiltersRef.current from dependencies

	// Pagination functions that manually trigger the fetch without state changes
	const goToNext = () => {
		pageRef.current += 1
		directionRef.current = "next"

		// Manually trigger the data fetch instead of relying on useEffect dependencies
		clearTimeout(timeoutRef.current)
		timeoutRef.current = setTimeout(() => {
			fetchJobRequestsData()
		}, 200)

		// Force a UI update to show the new page number
		triggerRender()
	}

	const goToPrevious = () => {
		pageRef.current -= 1
		directionRef.current = "previous"

		// Manually trigger the data fetch
		clearTimeout(timeoutRef.current)
		timeoutRef.current = setTimeout(() => {
			fetchJobRequestsData()
		}, 200)

		// Force a UI update to show the new page number
		triggerRender()
	}

	const clearFilters = () => {
		pageRef.current = 1
		directionRef.current = "next"
		lastVisibleRef.current = null
		firstVisibleRef.current = null
		resetFilters()
		setFiltered(false)
		clearFiltersRef.current = true

		// Manually trigger the data fetch
		clearTimeout(timeoutRef.current)
		timeoutRef.current = setTimeout(() => {
			fetchJobRequestsData()
		}, 200)

		// Force a UI update to show the new page number
		triggerRender()
	}

	const resetFilters = () => {
		setServiceTypeFilter([])
		setStatusFilter("")
		setBlackdomeStatusFilter("")
	}

	useEffect(() => {
		if (userPlus === "pending") {
			return
		}

		const getResponseIds = async () => {
			try {
				const jobResponseQuery = query(
					collection(db, "jobRequestResponses"),
					where("userId", "==", userPlus.id)
				)
				const jobResponseDocs = await getDocs(jobResponseQuery)
				const jobResponsesRequestIds = jobResponseDocs.docs.map((doc) => ({
					responseId: doc.data().id,
					requestId: doc.data().requestId,
				}))

				// ids of requests that this vendor has responded to
				setJobResponsesRequestIds(jobResponsesRequestIds)
			} catch (error) {
				console.error("Error fetching Job Request Responses:", error)
			}
		}
		getResponseIds()
	}, [userPlus])

	const getFilteredList = async () => {
		try {
			let filteredJobRequests = []
			// Create a base query with the common condition for isActive
			let baseQuery
			if (userPlus.partner && userPlus.isAdmin) {
				baseQuery = query(
					collection(db, "jobRequests"),
					where("partner", "==", userPlus.partner)
				)
			} else {
				baseQuery = query(
					collection(db, "jobRequests"),
					where("vendorIds", "array-contains", userPlus.id)
				)
			}
			// Add conditions based on serviceTypeFilter
			if (serviceTypeFilter.length > 0) {
				baseQuery = query(
					baseQuery,
					where("serviceType", "in", serviceTypeFilter)
				)
			}
			if (blackdomeStatusFilter !== "") {
				baseQuery = query(
					baseQuery,
					where("blackDomeStatus", "==", blackdomeStatusFilter)
				)
			}
			const docs = await getDocs(baseQuery)
			filteredJobRequests = docs.docs.map((doc) => doc.data())

			if (statusFilter !== "") {
				if (statusFilter === "todo") {
					filteredJobRequests = filteredJobRequests.filter(
						(request) =>
							!jobResponsesRequestIds.some(
								(response) => response.requestId === request.id
							) && !request.selectedResponseId
					)
				}
				if (statusFilter === "won") {
					filteredJobRequests = filteredJobRequests.filter(
						(request) =>
							request.selectedResponseId &&
							jobResponsesRequestIds.some(
								(response) =>
									response.responseId === request.selectedResponseId &&
									response.requestId === request.id
							)
					)
				}
				if (statusFilter === "received") {
					filteredJobRequests = filteredJobRequests.filter(
						(request) =>
							jobResponsesRequestIds.some(
								(response) => response.requestId === request.id
							) && !request.selectedResponseId
					)
				}
				if (statusFilter === "unsuccesful") {
					filteredJobRequests = filteredJobRequests.filter(
						(request) =>
							request.selectedResponseId &&
							!jobResponsesRequestIds.some(
								(response) => response.responseId === request.selectedResponseId
							)
					)
				}
			}
			setFiltered(true)
			setJobRequests(filteredJobRequests)
			onClose()
		} catch (error) {
			console.error("Error filtering job requests:", error)
		}
	}

	const statusFilterOptions = [
		{ label: "Response Needed", value: "todo" },
		{ label: "Jobs Won", value: "won" },
		{ label: "Client Received Response", value: "received" },
		{ label: "Unsuccesful Job Response", value: "unsuccesful" },
	]

	const openFilters = async () => {
		onOpen()
	}

	if (userPlus === null) {
		navigate("/login")
	}

	const handleOrderStatus = (jobRequest) => {
		setActiveJobRequest(jobRequest)
		setStatusModal(true)
	}

	const handleCloseModal = () => {
		setStatusModal(false)
		clearFiltersRef.current = true
		clearFilters()

		// Manually trigger the fetch instead of using state
		clearTimeout(timeoutRef.current)
		timeoutRef.current = setTimeout(() => {
			// This will execute fetchData without changing state
			const fetchData = async () => {
				if (isFetchingRef.current) return
				isFetchingRef.current = true
				setPageLoading(true)

				try {
					// Reset pagination
					pageRef.current = 1
					directionRef.current = "next"
					lastVisibleRef.current = null
					firstVisibleRef.current = null

					// Fetch services data
					let servicesQuery
					if (userPlus.partner && userPlus.isAdmin) {
						servicesQuery = query(
							collection(db, "services"),
							where("partner", "==", userPlus.partner)
						)
					} else {
						servicesQuery = query(
							collection(db, "services"),
							where("userId", "==", userPlus.id)
						)
					}
					const servicesDocs = await getDocs(servicesQuery)
					const services = servicesDocs.docs.map(
						(doc) => doc.data().serviceType
					)
					vendorServicesRef.current = services

					const serviceTypesOptions = servicesDocs.docs.map((doc) => ({
						value: doc.data().serviceType,
						label: doc.data().serviceType,
					}))
					serviceTypesOptionsRef.current = serviceTypesOptions

					// Fetch job requests data
					let jobRequestsQuery
					if (userPlus.partner && userPlus.isAdmin) {
						jobRequestsQuery = query(
							collection(db, "jobRequests"),
							where("partner", "==", userPlus.partner),
							orderBy("_createdAt"),
							limit(pageSize + 1)
						)
					} else {
						jobRequestsQuery = query(
							collection(db, "jobRequests"),
							where("vendorIds", "array-contains", userPlus.id),
							orderBy("_createdAt"),
							limit(pageSize + 1)
						)
					}

					const docs = await getDocs(jobRequestsQuery)

					// Update pagination state and job requests using refs
					if (!docs.empty) {
						const jobRequestsData = docs.docs.slice(0, pageSize).map((doc) => ({
							id: doc.id,
							...doc.data(),
						}))

						// Store the pagination info in refs
						lastVisibleRef.current = docs.docs[docs.docs.length - 1]
						firstVisibleRef.current = docs.docs[0]
						hasMoreRef.current = docs.docs.length > pageSize

						// This needs to update the UI, so we use state
						setJobRequests(jobRequestsData)

						const jobRequestIds = docs.docs.map((doc) => doc.data().id)
						jobRequestIdsRef.current = jobRequestIds
					}

					// Reset filters
					resetFilters()
					setFiltered(false)
				} catch (error) {
					console.error("Error fetching data:", error)
				} finally {
					setPageLoading(false)
					isFetchingRef.current = false
					clearFiltersRef.current = false
				}
			}

			fetchData()
		}, 200)
	}

	return (
		<>
			{!userPlus.activePlan && !userPlus.partner && (
				<PaywallBanner location="vendorJobRequestList" />
			)}
			<Box
				ml={{ base: "15px", lg: "93px" }}
				mr={{ base: "15px", lg: "93px" }}
				mt="30px"
			>
				<Heading size="lg" fontWeight="bold">
					{partner ? partner.jobRequestHeader : "Job Requests"}
				</Heading>
				<HStack
					display={["none", "none", "flex", "flex"]}
					justifyContent="space-between"
					mt="10px"
				>
					<Text>
						Track and manage your open{" "}
						{partner ? partner.jobRequestText : "job requests"}
					</Text>
					<Box display="flex" justifyContent="flex-end" gap={4}>
						<Button onClick={clearFilters} mt="15px">
							Clear Filters
						</Button>
						<Button
							onClick={() => openFilters()}
							rightIcon={<UpDownIcon />}
							bgColor="blue.900"
							color="white"
							mt="15px"
						>
							Filter
						</Button>
					</Box>
				</HStack>
				<Box
					display={["flex", "flex", "none", "none"]}
					mt="20px"
					flexDirection="column"
				>
					<Text mb="10px">
						Track and manage your open{" "}
						{partner ? partner.jobRequestText : "job requests"}
					</Text>
					<Box display="flex" justifyContent="flex-end" mb="20px">
						<Button
							onClick={() => openFilters()}
							rightIcon={<UpDownIcon />}
							bgColor="background"
							mt="15px"
						>
							Filter
						</Button>
						<Button onClick={clearFilters} mt="15px">
							Clear Filters
						</Button>
					</Box>
					{jobRequests.length === 0 && !filtered && (
						<EmptyState
							helperText={`You have not received any ${
								partner ? partner.jobRequestText : "job requests"
							} yet - invite your clients to start receiving ${
								partner ? partner.jobRequestText : "job requests"
							}!`}
						/>
					)}
					{jobRequests.length === 0 && filtered && (
						<Center m="auto">
							<Stack spacing={3} alignItems="center">
								<Image
									src={process.env.PUBLIC_URL + "/assets/search_empty.png"}
									h="200px"
									w="200px"
								/>
								<Heading fontWeight="semibold" size="lg">
									No results found
								</Heading>
								<Text align="center">
									There were not any results found matching your search or you
									are not connected with skilled vendors. Invite your vendors to
									join!
								</Text>
							</Stack>
						</Center>
					)}
					{jobRequests.map((jobRequest, index) => {
						return (
							<VendorJobRequestListMobileCard
								jobRequest={jobRequest}
								jobResponsesRequestIds={jobResponsesRequestIds}
								jobRequestIds={jobRequestIdsRef.current}
								vendorServices={vendorServicesRef.current}
								handleOrderStatus={handleOrderStatus}
								userPlus={userPlus}
								index={index}
								key={index}
							/>
						)
					})}
				</Box>
				<Box
					display={["none", "none", "flex", "flex"]}
					flexDirection="column"
					mt="30px"
				>
					{pageLoading && <SkeletonTable />}
					{!pageLoading && (
						<Table variant="simple">
							<Thead>
								<Tr>
									<Th>Job Info</Th>
									<Th>Service Type</Th>
									<Th> </Th>
									<Th>Job Status</Th>
									<Th>Actions</Th>
								</Tr>
							</Thead>
							<Tbody>
								{jobRequests.map((jobRequest, index) => {
									if (
										filtered &&
										(!vendorServicesRef.current.includes(
											jobRequest.serviceType
										) ||
											!userPlus.activePlan)
									) {
										return null
									}
									return (
										<Tr key={index}>
											<Td
												filter="auto"
												blur={
													!vendorServicesRef.current.includes(
														jobRequest.serviceType
													) ||
													(!userPlus.activePlan && !userPlus.partner)
														? "3px"
														: null
												}
											>
												<>
													<Text fontWeight="semibold">
														{jobRequest.locationAddress}
													</Text>
													<Text>{jobRequest.userEmail}</Text>
												</>
											</Td>
											<Td>{jobRequest.serviceType}</Td>
											<Td>
												{jobRequest?.servicePreferredDates &&
													jobRequest.servicePreferredDates.map(
														(entry, index) => (
															<HStack gap={5} mb="5px" key={index}>
																<Text key={index}>
																	{dateToFormat(entry.date)}
																</Text>
															</HStack>
														)
													)}
											</Td>
											{!jobRequest.blackDomeStatus && (
												<>
													<Td>
														{jobRequest.selectedResponseId &&
															jobResponsesRequestIds.some(
																(response) =>
																	response.responseId ===
																		jobRequest.selectedResponseId &&
																	response.requestId === jobRequest.id
															) && (
																<Tag
																	borderRadius="full"
																	variant="solid"
																	bgColor="green"
																	mr="5px"
																	mb="5px"
																>
																	<TagLabel>Request Booking</TagLabel>
																</Tag>
															)}
														{jobResponsesRequestIds.some(
															(response) => response.requestId === jobRequest.id
														) &&
															!jobRequest.selectedResponseId && (
																<Tag
																	borderRadius="full"
																	variant="solid"
																	bgColor="
														#FDE8C8"
																	color="black"
																	mr="5px"
																	mb="5px"
																>
																	<TagLabel>Client Received</TagLabel>
																</Tag>
															)}
														{!jobResponsesRequestIds.some(
															(response) => response.requestId === jobRequest.id
														) &&
															!jobRequest.selectedResponseId && (
																<Tag
																	borderRadius="full"
																	variant="solid"
																	bgColor="
													borderColor"
																	mr="5px"
																	mb="5px"
																>
																	<TagLabel>Response Needed</TagLabel>
																</Tag>
															)}
														{jobRequest.selectedResponseId &&
															!jobResponsesRequestIds.some(
																(response) =>
																	response.responseId ===
																	jobRequest.selectedResponseId
															) && (
																<Tag
																	borderRadius="full"
																	variant="solid"
																	bgColor="
													#gray.500"
																	mr="5px"
																	mb="5px"
																>
																	<TagLabel>Unsuccessful</TagLabel>
																</Tag>
															)}
													</Td>
												</>
											)}
											{jobRequest.blackDomeStatus && (
												<Td>
													<Tag
														borderRadius="full"
														variant="solid"
														bgColor={
															BlackDomeStatuses.find(
																(status) =>
																	status.value === jobRequest.blackDomeStatus
															).color
														}
														mr="5px"
														mb="5px"
													>
														<TagLabel>
															{
																BlackDomeStatuses.find(
																	(status) =>
																		status.value === jobRequest.blackDomeStatus
																).label
															}
														</TagLabel>
													</Tag>
												</Td>
											)}
											<Td>
												<Menu>
													<MenuButton
														as={IconButton}
														bgColor="background"
														aria-label="Options"
														icon={<EditIcon />}
													/>
													<MenuList>
														<MenuItem
															as={NavLink}
															to={`/job-request/${jobRequest.id}`}
														>
															{`Respond to ${
																partner
																	? partner.jobRequestHeaderSingular
																	: "Job Request"
															}`}
														</MenuItem>
														<MenuItem
															as={NavLink}
															to={`/job-requests/invoice/${jobRequest.id}`}
															state={{ jobRequest }}
														>
															Add Invoice
														</MenuItem>
														<MenuItem
															as={NavLink}
															to={`/job-requests/completion/${jobRequest.id}`}
															state={{ jobRequest }}
														>
															Add Completed Photos
														</MenuItem>
														{((userPlus.partner === "BDPS" &&
															userPlus.isAdmin) ||
															userPlus.integrations?.includes("PPW")) && (
															<MenuItem
																as={Button}
																fontWeight="normal"
																onClick={() => handleOrderStatus(jobRequest)}
															>
																Update Status of Work Order
															</MenuItem>
														)}
													</MenuList>
												</Menu>
											</Td>
										</Tr>
									)
								})}
							</Tbody>
						</Table>
					)}
					{jobRequests.length === 0 && !filtered && (
						<EmptyState
							helperText={`You have not received any ${
								partner ? partner.jobRequestText : "job requests"
							} yet - invite your clients to start receiving ${
								partner ? partner.jobRequestText : "job requests"
							}!`}
						/>
					)}

					{jobRequests.length === 0 && filtered && (
						<Center m="auto">
							<Stack spacing={3} alignItems="center">
								<Image
									src={process.env.PUBLIC_URL + "/assets/search_empty.png"}
									h="200px"
									w="200px"
								/>
								<Heading fontWeight="semibold" size="lg">
									No results found
								</Heading>
								<Text align="center">
									There were not any results found matching your search or you
									are not connected with skilled vendors. Invite your vendors to
									join!
								</Text>
							</Stack>
						</Center>
					)}
				</Box>
				{jobRequests.length > 0 && (
					<Box display="flex" justifyContent="flex-end" gap={3} my="15px">
						<Button
							onClick={() => goToPrevious()}
							isDisabled={pageRef.current === 1 || filtered}
						>
							Previous
						</Button>
						<Button
							onClick={() => goToNext()}
							isDisabled={!hasMoreRef.current || filtered}
						>
							Next
						</Button>
					</Box>
				)}
			</Box>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				size={{ base: "full", lg: "2xl" }}
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>
						Filter {partner ? partner.jobRequestHeader : "Job Requests"}
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Stack gap={2}>
							<Box>
								<Select
									placeholder="Service Type"
									value={serviceTypesOptionsRef.current.filter((option) =>
										serviceTypeFilter.includes(option.value)
									)}
									isMulti
									options={serviceTypesOptionsRef.current}
									onChange={(options) => {
										setServiceTypeFilter(options.map((option) => option.value))
									}}
								/>
							</Box>

							{(userPlus.partner === "BDPS" && userPlus.isAdmin) ||
							userPlus.integrations?.includes("PPW") ? (
								<Box>
									<Select
										placeholder="PPW Status"
										value={BlackDomeStatuses.filter(
											(option) => option.value === blackdomeStatusFilter
										)}
										options={BlackDomeStatuses}
										onChange={(options) => {
											setBlackdomeStatusFilter(options.value)
										}}
									/>
								</Box>
							) : (
								<Box>
									<Select
										placeholder={`${
											partner ? partner.jobRequestHeaderSingular : "Job Request"
										} Status`}
										value={statusFilterOptions.filter(
											(option) => option.value === statusFilter
										)}
										options={statusFilterOptions}
										onChange={(options) => {
											setStatusFilter(options.value)
										}}
									/>
								</Box>
							)}
						</Stack>
					</ModalBody>
					<ModalFooter margin="auto">
						<Button
							bgColor="borderColor"
							color="white"
							onClick={getFilteredList}
						>
							Apply Filters
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
			{statusModal && (
				<BlackDomeStatusModal
					jobRequest={activeJobRequest}
					isOpen={statusModal}
					onClose={handleCloseModal}
				/>
			)}
		</>
	)
}
